import { ChangeDetectorRef, Component, ContentChild, Input, OnDestroy, OnInit, TemplateRef } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, merge, Subject, switchMap, takeUntil, tap } from "rxjs";

import { Role } from "../../../users/models/users.entity";
import { allAdditionnalThematics, allMainThematics, contentDurations, contentFormats, pillars } from "../../models/contents.entity";
import { ContentsRepository } from "../../repositories/contents.repository";
import { TabStoreService } from "../../services/content-tab.store";

@Component({
    selector: 'app-contents-card-pagination',
    templateUrl: './contents-card-pagination.component.html',
    styleUrls: ['./contents-card-pagination.component.scss']
})

export class ContentsCardPaginationComponent <
  Entity extends { id: string }
> implements OnInit, OnDestroy {
    
    scroll$ = new Subject<void>();

    pageSize$: BehaviorSubject<number> = new BehaviorSubject<number>(5);

    page$: BehaviorSubject<number> = new BehaviorSubject<number>(1);

    items$ = new BehaviorSubject<Entity[]>([]);

    newItems$ = new Subject<Entity[]>();

    loading$ = new Subject<void>();

    protected destroy$ = new Subject<void>();

    @ContentChild("card", { static: false }) cardTemplateRef!: TemplateRef<any>;

    @Input("unique-key") uniqueKey: keyof Entity = "id";

    nextPageLoading = false;

    totalCount: number = 0;

    loading: boolean = false;

    private _apiLoading = new BehaviorSubject<boolean>(false);
  
    apiLoading$ = this._apiLoading.asObservable();

    // Note: Objective-based tabs merged into one per REMOVE-OBJECTIF.txt requirements
    // Now only 2 tabs: All resources (tab 1) + Ma liste (tab 2)
    selectedTab: number = 1;

    // Show all objectives except 'prepare' (which is for formations)
    objectives: string[] = ['inspire', 'act', 'transform'];

    contentsForm!: FormGroup;
    
    searchForm: FormControl<string | null> = new FormControl<string>("");

    isRestricted!: boolean;

    canSeePage = true;

    userProfile: any;

    isDropdownVisible = false;

    contentsFilterForm!: FormGroup;

    page = 1;

    limit = 5;

    private currentItemsLength = 0;

    initialLoad = true;

    constructor(private router: Router, private route: ActivatedRoute, public readonly contentsRepository: ContentsRepository, private fb: FormBuilder, private cdr: ChangeDetectorRef, private tabStoreService: TabStoreService) { }

    // Note: fetchTabContent and selectTab removed - tabs removed per REMOVE-OBJECTIF.txt requirements
    // All resources now appear on a single page with optional favorites filter

    /**
     * Fetch resources based on selected tab
     * Tab 1: All resources (all objectives except 'prepare')
     * Tab 2: Ma liste (favorites only)
     */
    fetchContent() {
        this.totalCount = 0;
        this.page = 1;

        const themes = [...this.contentsForm.value.allMainThematics, ...this.contentsForm.value.allAdditionnalThematics].filter((theme: any) => theme.value).map((theme: any) => theme.key);
        const formatsFiltered = this.contentsForm.value.formats.filter((format: any) => format.value).map((format: any) => format.key);
        const durationsFiltered = this.contentsForm.value.durations.filter((duration: any) => duration.value).map((duration: any) => duration.key);
        const pillarsFiltered = this.contentsForm.value.pillars.filter((pillar: any) => pillar.value).map((pillar: any) => pillar.key);
        this._apiLoading.next(true);

        // Tab 2 = Ma liste (favorites)
        const isFavoritesTab = this.selectedTab === 2;

        this.contentsRepository.talkerPaginate({
          page: this.page,
          pageSize: this.limit,
          search: this.searchForm.value?.trim(),
          properties: {
            ...(isFavoritesTab ? { isFavorite: true } : {}),
            objectives: this.objectives,
            pillars: pillarsFiltered,
            durations: durationsFiltered,
            formats: formatsFiltered,
            themes
          }
        }).subscribe({
          next: (contents: any) => {
              this.items$.next(contents.items);
              this.currentItemsLength = contents.items.length;
              this.isRestricted = contents.isRestricted;
              this.totalCount = contents.totalCount;
              this.nextPageLoading = false;
              this.loading = false;
              this._apiLoading.next(false);
              this.initialLoad = false;
          },
          error: () => {
            this.loading = false;
            this._apiLoading.next(false);
          }
        });
    }

    /**
     * Select tab (Tab 1: All resources, Tab 2: Ma liste)
     */
    selectTab(tabIndex: number) {
        this.selectedTab = tabIndex;
        this.tabStoreService.setSelectedTab(tabIndex);
        this.loading = true;
        this.loading$.next();
        this.items$.next([]);
        this.page$.next(1);
        this.currentItemsLength = 0;

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: tabIndex },
            queryParamsHandling: 'merge'
        });

        this.fetchContent();
    }

    ngOnInit(): void {
      this.initializeForms();
        this.route.data
        .pipe(
            map(data => data.profile),
            takeUntil(this.destroy$)

        ).subscribe((profile: any) => {
          this.userProfile = profile;
          this.canSeePage = profile.role === Role.Sherpa ? true : !!profile?.organization?.subscriptionPlan;

          // Handle tab from query params or store (now only 2 tabs: 1=All, 2=Ma liste)
          this.route?.queryParams?.subscribe(params => {
              const storedTab = this.tabStoreService.getSelectedTab();
              // Map old tabs (1-4) to new tabs (1-2): tabs 1-3 become 1, tab 4 becomes 2
              let tab = +params.tab || storedTab || 1;
              if (tab >= 3) tab = 1; // Old tabs 3,4 -> map to appropriate new tab
              if (tab === 4) tab = 2; // Old "Ma liste" tab 4 -> new tab 2
              this.selectedTab = tab > 2 ? 1 : tab; // Ensure valid tab (1 or 2)
              this.fetchContent();
          });

          this.subscribeToChanges();
      });
    }

    private initializeForms() {
      this.contentsForm = this.fb.group({
        allMainThematics: this.fb.array(this.createItems(allMainThematics)),
        allAdditionnalThematics: this.fb.array(this.createItems(allAdditionnalThematics)),
        formats: this.fb.array(this.createItems(contentFormats)),
        durations: this.fb.array(this.createItems(contentDurations)),
        pillars: this.fb.array(this.createItems(pillars)),
      });
          
      this.contentsFilterForm = this.fb.group({
        durationMinimum: new FormControl('', { nonNullable: true }),
        durationMaximum: new FormControl('', { nonNullable: true }),
      });
    }
    
    ngOnDestroy() {
        this.destroy$.next();
    }
    
    onScrollEvent() {
        this.scroll$.next();
    }
    
    public loadNextPage(): void {
      if (!this.nextPageLoading && this.currentItemsLength < this.totalCount) {
        if(this.page$.getValue() === 1 && this.currentItemsLength === 0) {
          return;
        }
        this.nextPageLoading = true; 
        this.page$.next(this.page$.getValue() + 1);
      }
    }

    private subscribeToChanges() {
      merge(
        this.page$.pipe(
          distinctUntilChanged()
        ),
        this.contentsForm.valueChanges.pipe(
          tap(() => {
            if (this.initialLoad) return;
            this.items$.next([]);
            this.page$.next(1);
            this.currentItemsLength = 0;
          }),
          debounceTime(300),
          distinctUntilChanged()
        ),
        this.contentsFilterForm.valueChanges.pipe(
          tap(() => {
            if (this.initialLoad) return;
            this.items$.next([]);
            this.page$.next(1);
            this.currentItemsLength = 0;
          }),
          debounceTime(300),
          distinctUntilChanged()
        )
      ).pipe(
        tap(() => {
          if (this.initialLoad) {
            return;
          }
          this.loading$.next();
        }),
        debounceTime(300),
        switchMap(() => {
          const themes = [...this.contentsForm.value.allMainThematics, ...this.contentsForm.value.allAdditionnalThematics].filter((theme: any) => theme.value).map((theme: any) => theme.key);
          const formatsFiltered = this.contentsForm.value.formats.filter((format: any) => format.value).map((format: any) => format.key);
          const durationsFiltered = this.contentsForm.value.durations.filter((duration: any) => duration.value).map((duration: any) => duration.key);
          const pillarsFiltered = this.contentsForm.value.pillars.filter((pillar: any) => pillar.value).map((pillar: any) => pillar.key);
          this._apiLoading.next(true);
          // Tab 2 = Ma liste (favorites)
          const isFavoritesTab = this.selectedTab === 2;
          return this.contentsRepository.talkerPaginate({
            page: this.page$.getValue(),
            pageSize: this.limit,
            properties: {
              ...(isFavoritesTab ? { isFavorite: true } : {}),
              objectives: this.objectives,
              pillars: pillarsFiltered,
              durations: durationsFiltered,
              formats: formatsFiltered,
              themes
            }
          });
        }),
        takeUntil(this.destroy$)
      ).subscribe({
        next: (contents: any) => {
          if (this.page$.getValue() === 1 || !this.items$.getValue()?.length) {
            this.items$.next(contents.items);
            this.currentItemsLength = contents.items.length;
          } else {
            this.newItems$.next(contents.items);
            this.currentItemsLength += contents.items.length;
          }
          this.isRestricted = contents.isRestricted;
          this.totalCount = contents.totalCount;
          this.nextPageLoading = false;
          this.loading = false;
          this._apiLoading.next(false);
        },
        error: () => {
          this.loading = false;
          this.nextPageLoading = false;
        }
      });
    }
    
    toggleSelection(item: any): void {
        const pillar: any = this.pillars.controls.find((control: any) => control.value.key === item.key);
        if (pillar) {
          pillar.patchValue({ value: !pillar.value.value });
        } 
    }
    
    createItems(thematicsPart: any[]): FormGroup[] {
        return thematicsPart.map(item => this.fb.group({
        name: [item.label],
        value: [item.selected],
        key: [item.key],
        icon: [item.icon]
        }));
    }
    
    openMoreThematic(event: Event){
        event.preventDefault();
        this.isDropdownVisible = !this.isDropdownVisible;
    }
    
    handleEnterPressed(value: string) {
        (window._mtm || []).push({ 'event' : 'recherche-ressource', 'motcle' : `${value.trim()}` });
        const themes = [...this.contentsForm.value.allMainThematics, ...this.contentsForm.value.allAdditionnalThematics].filter((theme: any) => theme.value).map((theme: any) => theme.key);
        const formatsFiltered = this.contentsForm.value.formats.filter((format: any) => format.value).map((format: any) => format.key);
        const durationsFiltered = this.contentsForm.value.durations.filter((duration: any) => duration.value).map((duration: any) => duration.key);
        const pillarsFiltered = this.contentsForm.value.pillars.filter((pillar: any) => pillar.value).map((pillar: any) => pillar.key);
        this._apiLoading.next(true);

        // Tab 2 = Ma liste (favorites)
        const isFavoritesTab = this.selectedTab === 2;

        this.contentsRepository.talkerPaginate({
          page: this.page,
          pageSize: this.limit,
          search: value.trim(),
          properties: {
            ...(isFavoritesTab ? { isFavorite: true } : {}),
            objectives: this.objectives,
            pillars: pillarsFiltered,
            durations: durationsFiltered,
            formats: formatsFiltered,
            themes
          }
        }).subscribe({
          next: (contents: any) => {
            this.items$.next(contents.items);
            this.currentItemsLength = contents.items.length;
            this.isRestricted = contents.isRestricted;
            this.totalCount = contents.totalCount;
            this.nextPageLoading = false;
            this.loading = false;
            this._apiLoading.next(false);
          },
          error: () => {
            this._apiLoading.next(false);
          }
        });
    }

    onCurrentItemsLengthChange(length: number) {
      this.currentItemsLength = length;
    }

    handleFavoriteChange(event: {contentId: string, isFavorited: boolean}){
      // Refresh content when favorite status changes (if viewing Ma liste tab)
      // When user unfavorites a content while on Ma liste tab, refresh the list
      if(!event.isFavorited && this.selectedTab === 2){
        this.fetchContent();
      }
    }
    
    get pillars() {
        return this.contentsForm.get('pillars') as FormArray;
    }


    get formats() {
        return this.contentsForm.get('formats') as FormArray;
    }

    get durations() {
        return this.contentsForm.get('durations') as FormArray;
    }

    get thematics(){
        return this.contentsForm.get('allMainThematics') as FormArray;
    }

    get additionnalThematics(){
        return this.contentsForm.get('allAdditionnalThematics') as FormArray;
    }
}